import { useState, useRef } from 'react';
import { Button, Input } from 'animal-island-ui';
import { useAppStore } from '../store/useAppStore';
import { EVENT_TYPES } from '../data/mockData';
import { EVENT_ICONS } from '../icons';
import { GOD_IMAGES } from '../godImages';
import type { EventType } from '../types';
import AuthDialog from './AuthDialog';

export default function Onboarding() {
  const { username, setUsername, setStage, createProject, authToken } = useAppStore();

  const [step, setStep] = useState<'name' | 'event' | 'done'>('name');
  const [nameInput, setNameInput] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);
  const [showDots, setShowDots] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const pendingEventRef = useRef<EventType | null>(null);

  const handleNameConfirm = () => {
    const name = nameInput.trim() || '有缘人';
    setUsername(name);
    setStep('event');
  };

  // 真正创建项目（调后端 POST /events，需登录）
  const doCreate = async (type: EventType) => {
    try {
      await createProject(type);
    } catch {
      /* 创建失败也进入主界面，用户可在主界面重试 */
    }
    setFadeOut(true);
    setTimeout(() => setStage('main'), 1000);
  };

  const handleEventSelect = (type: EventType) => {
    setSelectedEvent(type);
    setStep('done');
    setShowDots(true);
    setTimeout(() => {
      setShowDots(false);
      // 未登录先弹登录/注册，登录成功后再建项；已登录直接建项
      if (!authToken) {
        pendingEventRef.current = type;
        setAuthDialogOpen(true);
      } else {
        doCreate(type);
      }
    }, 3000);
  };

  const handleAuthSuccess = () => {
    const pending = pendingEventRef.current;
    if (pending) {
      pendingEventRef.current = null;
      doCreate(pending);
    }
  };

  return (
    <>
      <AuthDialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
        onSuccess={handleAuthSuccess}
        initialMode="register"
      />
      <div
        id="onboarding-container"
        className={`flex flex-col items-center justify-center min-h-screen p-4 transition-opacity duration-1000 ${
          fadeOut ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ background: 'linear-gradient(180deg, #a8dadc 0%, #fdf6e3 40%)' }}
      >
        <div className="flex gap-6 items-center max-w-2xl w-full">
          {/* 喜神形象 */}
          <div id="onboarding-god-image" className="flex-shrink-0">
            <img
              src={GOD_IMAGES.xishen}
              alt="喜神"
              className="w-80 h-80 object-contain bounce-in"
              style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' }}
            />
          </div>

          {/* 对话区域 */}
          <div
            id="onboarding-dialog"
            className="flex-1 flex flex-col gap-4 max-h-[70vh] overflow-y-auto"
            style={{ scrollBehavior: 'smooth' }}
          >
            {/* 喜神问话 — 所有步骤都显示 */}
            <div id="onboarding-god-question" className="text-lg text-gray-700">
              这位凡人，误闯天家了！你是谁呀？
            </div>

            {/* 名字输入 — step === 'name' */}
            {step === 'name' && (
              <>
                <div id="onboarding-name-input">
                  <Input
                    size="middle"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleNameConfirm()}
                    placeholder="输入你的名字"
                    className="w-full"
                    suffix={<span className="text-xs text-gray-400 whitespace-nowrap">点击回车确认</span>}
                    autoFocus
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">已有账号</span>
                  <Button type="primary" onClick={() => setAuthDialogOpen(true)}>登录</Button>
                </div>
              </>
            )}

            {/* 名字气泡 — step !== 'name' */}
            {step !== 'name' && (
              <div id="onboarding-name-bubble" className="chat-bubble self-end text-sm" style={{ background: '#fff', border: '2px solid #e0d5b8' }}>
                我叫<span style={{ color: '#e76f51', fontWeight: 'bold' }}>{username}</span>。
              </div>
            )}

            {/* 喜神介绍 — step === 'event' 或 'done' */}
            {(step === 'event' || step === 'done') && (
              <div id="onboarding-god-intro" className="text-lg text-gray-700">
                噢！我可是喜神，今日你能撞见我，定是有何喜事了？
              </div>
            )}

            {/* 喜事选择 — step === 'event' */}
            {step === 'event' && (
              <div id="onboarding-event-select" className="flex flex-wrap gap-3">
                {EVENT_TYPES.map((event) => (
                  <button
                    key={event.type}
                    id={`onboarding-event-${event.type}`}
                    onClick={() => handleEventSelect(event.type)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      selectedEvent === event.type
                        ? 'border-red-400 bg-red-50 scale-105'
                        : 'border-amber-200 bg-amber-50/50 hover:bg-amber-100 hover:border-amber-300'
                    }`}
                  >
                    <img src={EVENT_ICONS[event.type]} alt={event.label} className="w-9 h-9 object-contain" />
                    <span className="text-xs font-medium text-gray-700">{event.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* 喜神最终宣布 — step === 'done' */}
            {step === 'done' && (
              <div id="onboarding-god-final" className="text-lg text-gray-700">
                <span style={{ color: '#e63946', fontWeight: 'bold' }}>喜事啊！</span>
                我拉几个神仙组成工作组，把你这大喜事办好！
              </div>
            )}

            {/* 省略号跳动动画 */}
            {showDots && (
              <div className="flex gap-1 items-center">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
